import { create } from 'zustand';
import { useNotificationsStore } from '@/stores/notifications.store';
import * as api from './worlds.api';
import { ChunkGrid } from './map/chunkGrid';
import { decodeRwg1 } from './map/rwg1';
import type {
  Biome, Country, GeoRegion, Level, Marker, Possession, RosterSyncState, WorldsState,
} from './worlds.types';

// ============================================================
// Mondes (NationsGlory) : état partagé des onglets
// ============================================================
// Remis à zéro au montage de la page (et au changement de projet) : rien ne survit d'une visite à
// l'autre, la page repart toujours de l'état serveur.
//
// Chaque liste garde la version sous laquelle elle a été chargée. Le polling de /state (10 s) ne
// recharge que ce dont la version a bougé ; la grille passe en delta (since = version locale).
// Les écritures de l'utilisateur remplacent l'élément renvoyé par le serveur sans attendre le poll.
// ============================================================

/** Toasts du panel (NotificationsContainer d'AdminLayout), appelables hors composant. */
export const toast = {
  success: (m: string) => useNotificationsStore.getState().success(m),
  error: (m: string) => useNotificationsStore.getState().error(m, 8000),
  info: (m: string) => useNotificationsStore.getState().info(m),
  warning: (m: string) => useNotificationsStore.getState().warning(m, 8000),
};

/** Signal de rafraîchissement de la couche des chunks. */
export interface GridChange {
  full: boolean;
  keys: number[];
}

interface WorldsStore {
  state: WorldsState | null;
  /** Échec de /state (module absent, satellite trop ancien...) : message affiché tel quel. */
  stateError: string | null;
  loading: boolean;
  /** Niveau courant : carte, compteurs des pays et marqueurs. */
  level: string | null;

  levels: Level[];
  levelsVersion: number;
  countries: Country[];
  countriesVersion: number;
  /** Version de grille sous laquelle chunkCount / bbox ont été calculés. */
  countriesGridVersion: number;
  geoRegions: GeoRegion[];
  geoRegionsVersion: number;
  biomes: Biome[];
  biomesVersion: number;
  markers: Marker[];
  markersVersion: number;
  markersLevel: string | null;

  grid: ChunkGrid | null;
  gridRev: number;
  gridChange: GridChange | null;
  gridError: string | null;

  possession: Possession | null;
  roster: RosterSyncState | null;

  reset: () => void;
  init: () => Promise<void>;
  poll: () => Promise<void>;
  setLevel: (name: string) => Promise<void>;
  reloadLevels: () => Promise<void>;
  reloadCountries: () => Promise<void>;
  reloadGeoRegions: () => Promise<void>;
  reloadBiomes: () => Promise<void>;
  reloadMarkers: () => Promise<void>;
  /** Rattrape la grille du niveau courant (delta depuis la version locale). */
  syncGrid: () => Promise<void>;
  loadPossession: () => Promise<void>;
  loadRoster: () => Promise<void>;

  putCountry: (c: Country) => void;
  dropCountry: (id: string) => void;
  putGeoRegion: (r: GeoRegion) => void;
  dropGeoRegion: (id: string) => void;
  putLevel: (l: Level) => void;
  dropLevel: (name: string) => void;
  putBiome: (b: Biome) => void;
  dropBiome: (name: string) => void;
  putMarker: (m: Marker) => void;
  dropMarker: (id: string) => void;
}

const EMPTY = {
  state: null,
  stateError: null,
  loading: false,
  level: null,
  levels: [],
  levelsVersion: -1,
  countries: [],
  countriesVersion: -1,
  countriesGridVersion: -1,
  geoRegions: [],
  geoRegionsVersion: -1,
  biomes: [],
  biomesVersion: -1,
  markers: [],
  markersVersion: -1,
  markersLevel: null,
  grid: null,
  gridRev: 0,
  gridChange: null,
  gridError: null,
  possession: null,
  roster: null,
};

/** Jeton de session : une réponse arrivée après un reset (changement de projet) est ignorée. */
let session = 0;

// Rattrapage de grille sérialisé : un appel pendant un autre en relance un seul à la fin.
let gridInFlight: Promise<void> | null = null;
let gridAgain = false;

function gridVersionOf(state: WorldsState | null, level: string | null): number | null {
  if (!state || !level) return null;
  return state.levels.find((l) => l.name === level)?.gridVersion ?? null;
}

function byName<T extends { name: string }>(a: T, b: T): number {
  return a.name.localeCompare(b.name);
}

export const useWorldsStore = create<WorldsStore>((set, get) => ({
  ...EMPTY,

  reset: () => {
    session++;
    gridInFlight = null;
    gridAgain = false;
    set({ ...EMPTY });
  },

  init: async () => {
    const my = ++session;
    gridInFlight = null;
    gridAgain = false;
    set({ ...EMPTY, loading: true });
    const res = await api.getState();
    if (my !== session) return;
    if (!res.data) {
      set({ loading: false, stateError: res.error ?? 'Module Mondes indisponible' });
      return;
    }
    const state = res.data;
    const level = state.mainLevel;
    set({ state, level, grid: level ? new ChunkGrid(level) : null });
    await Promise.all([
      get().reloadLevels(),
      get().reloadCountries(),
      get().reloadGeoRegions(),
      get().reloadBiomes(),
      get().reloadMarkers(),
      get().syncGrid(),
    ]);
    if (my !== session) return;
    set({ loading: false });
  },

  poll: async () => {
    if (get().loading) return;
    const my = session;
    const res = await api.getState();
    if (my !== session || !res.data) return;
    const prev = get().state;
    const state = res.data;
    // Premier succès après un échec (satellite redémarré, module activé) ou passage vide → rempli
    // (import depuis un autre onglet) et l'inverse : tout recharger.
    if (!prev || prev.empty !== state.empty) { await get().init(); return; }
    set({ state, stateError: null });

    let level = get().level;
    if (!level || !state.levels.some((l) => l.name === level)) {
      // Niveau courant supprimé ailleurs (ou premier niveau créé) : revenir au principal.
      level = state.mainLevel;
      if (level !== get().level) { if (level) await get().setLevel(level); else set({ level: null, grid: null }); return; }
    }
    const s = get();
    const gv = gridVersionOf(state, level);
    const jobs: Promise<void>[] = [];
    if (state.versions.levels !== s.levelsVersion) jobs.push(s.reloadLevels());
    if (state.versions.geoRegions !== s.geoRegionsVersion) jobs.push(s.reloadGeoRegions());
    if (state.versions.biomes !== s.biomesVersion) jobs.push(s.reloadBiomes());
    if (state.versions.markers !== s.markersVersion || s.markersLevel !== level) jobs.push(s.reloadMarkers());
    if (state.versions.countries !== s.countriesVersion || (gv != null && gv !== s.countriesGridVersion)) {
      jobs.push(s.reloadCountries());
    }
    if (gv != null && s.grid && gv !== s.grid.version) jobs.push(s.syncGrid());
    await Promise.all(jobs);
  },

  setLevel: async (name) => {
    // La requête de grille de l'ancien niveau peut encore arriver : syncGrid l'écarte (grid !== grid).
    gridInFlight = null;
    gridAgain = false;
    set({ level: name, grid: new ChunkGrid(name), gridRev: get().gridRev + 1, gridChange: { full: true, keys: [] }, markers: [], markersLevel: null });
    await Promise.all([get().reloadCountries(), get().reloadMarkers(), get().syncGrid()]);
  },

  reloadLevels: async () => {
    const my = session;
    const res = await api.listLevels();
    if (my !== session || !res.data) return;
    const items = [...res.data.items].sort((a, b) => a.sortOrder - b.sortOrder || a.name.localeCompare(b.name));
    set({ levels: items, levelsVersion: res.data.version });
  },

  reloadCountries: async () => {
    const my = session;
    const level = get().level;
    const res = await api.listCountries(level);
    if (my !== session || !res.data || get().level !== level) return;
    const items = [...res.data.items].sort((a, b) => (a.slug < b.slug ? -1 : a.slug > b.slug ? 1 : 0));
    set({ countries: items, countriesVersion: res.data.version, countriesGridVersion: res.data.gridVersion });
  },

  reloadGeoRegions: async () => {
    const my = session;
    const res = await api.listGeoRegions();
    if (my !== session || !res.data) return;
    set({ geoRegions: [...res.data.items].sort(byName), geoRegionsVersion: res.data.version });
  },

  reloadBiomes: async () => {
    const my = session;
    const res = await api.listBiomes();
    if (my !== session || !res.data) return;
    set({ biomes: [...res.data.items].sort(byName), biomesVersion: res.data.version });
  },

  reloadMarkers: async () => {
    const my = session;
    const level = get().level;
    if (!level) { set({ markers: [], markersLevel: null }); return; }
    const res = await api.listMarkers(level);
    if (my !== session || !res.data || get().level !== level) return;
    set({ markers: res.data.items, markersVersion: res.data.version, markersLevel: level });
  },

  syncGrid: async () => {
    if (gridInFlight) { gridAgain = true; return gridInFlight; }
    const my = session;
    const run = async () => {
      do {
        gridAgain = false;
        const grid = get().grid;
        if (!grid) return;
        const res = await api.getGrid(grid.level, grid.version);
        if (my !== session || get().grid !== grid) return;
        if (!res.data) { set({ gridError: res.error ?? 'Grille indisponible' }); return; }
        let applied;
        try {
          applied = grid.apply(await decodeRwg1(res.data));
        } catch (e) {
          set({ gridError: e instanceof Error ? e.message : 'Grille illisible' });
          return;
        }
        if (my !== session || get().grid !== grid) return;
        // Compteurs des pays tenus à jour localement : pas besoin de recharger la liste pour voir
        // le nombre de chunks bouger sous le pinceau (bbox et centerInside suivront au poll).
        let countries = get().countries;
        if (applied.delta.size > 0 && countries.length > 0) {
          if (applied.full) {
            const counts = grid.counts();
            countries = countries.map((c) => (c.chunkCount === (counts.get(c.idx) ?? 0) ? c : { ...c, chunkCount: counts.get(c.idx) ?? 0 }));
          } else {
            countries = countries.map((c) => {
              const d = applied.delta.get(c.idx);
              return d ? { ...c, chunkCount: Math.max(0, c.chunkCount + d) } : c;
            });
          }
        }
        set({
          countries,
          gridError: null,
          gridRev: get().gridRev + 1,
          gridChange: { full: applied.full, keys: applied.changed },
        });
      } while (gridAgain);
    };
    gridInFlight = run().finally(() => { gridInFlight = null; });
    return gridInFlight;
  },

  loadPossession: async () => {
    const my = session;
    const res = await api.getPossession();
    if (my !== session) return;
    set({ possession: res.data ?? { available: false, message: res.error ?? 'Possession indisponible', fetchedAt: null, items: [] } });
  },

  loadRoster: async () => {
    const my = session;
    const res = await api.getRoster();
    if (my !== session || !res.data) return;
    set({ roster: res.data });
  },

  putCountry: (c) => set((s) => {
    const i = s.countries.findIndex((x) => x.id === c.id);
    // Les champs calculés de la réponse valent pour le niveau principal : on garde ceux du niveau
    // courant tant que le poll n'a pas rechargé la liste.
    if (i >= 0) {
      const old = s.countries[i];
      const keep = s.state?.mainLevel !== s.level;
      const next = keep ? { ...c, chunkCount: old.chunkCount, bbox: old.bbox, centerInside: old.centerInside } : c;
      const list = [...s.countries];
      list[i] = next;
      return { countries: list };
    }
    return { countries: [...s.countries, c].sort((a, b) => (a.slug < b.slug ? -1 : a.slug > b.slug ? 1 : 0)) };
  }),
  dropCountry: (id) => set((s) => ({ countries: s.countries.filter((c) => c.id !== id) })),
  putGeoRegion: (r) => set((s) => ({ geoRegions: [...s.geoRegions.filter((x) => x.id !== r.id), r].sort(byName) })),
  dropGeoRegion: (id) => set((s) => ({
    geoRegions: s.geoRegions.filter((r) => r.id !== id),
    countries: s.countries.map((c) => (c.geoRegionId === id ? { ...c, geoRegionId: null } : c)),
  })),
  putLevel: (l) => set((s) => ({
    levels: [...s.levels.filter((x) => x.name !== l.name), l].sort((a, b) => a.sortOrder - b.sortOrder || a.name.localeCompare(b.name)),
  })),
  dropLevel: (name) => set((s) => ({ levels: s.levels.filter((l) => l.name !== name) })),
  putBiome: (b) => set((s) => ({ biomes: [...s.biomes.filter((x) => x.name !== b.name), b].sort(byName) })),
  dropBiome: (name) => set((s) => ({ biomes: s.biomes.filter((b) => b.name !== name) })),
  putMarker: (m) => set((s) => ({ markers: [...s.markers.filter((x) => x.id !== m.id), m] })),
  dropMarker: (id) => set((s) => ({ markers: s.markers.filter((m) => m.id !== id) })),
}));
